import { AxiosResponse } from "axios";
import classNames from "classnames";
import type { Thread } from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";

import { DeleteRequest } from "shared/api/api";
import { useIsOverflowing } from "shared/hooks";

import Tooltip from "features/components/Tooltip";
import DeleteAction from "features/ui/DeleteAction";

import { getThreadTitle } from "./utils";

interface ThreadListItemProps {
  thread: Thread;
  isSelected: boolean;
  isDisabled: boolean;
  deleteThread: (args: DeleteRequest) => Promise<AxiosResponse<any, any, {}>>;
  onClick: () => void;
  onDelete: (threadID: string) => void;
}

const ThreadListItem = ({
  thread,
  isSelected,
  isDisabled,
  onClick,
  deleteThread,
  onDelete,
}: ThreadListItemProps) => {
  const { t } = useTranslation("duck");
  const title = getThreadTitle(thread, t("empty_chat"));
  const { ref: titleRef, isOverflowing } = useIsOverflowing();
  const innerItem = (
    <div
      className={classNames(
        "px-4 hover:bg-slate-100 flex !min-h-10 !h-10 gap-2 group items-center text-left w-full",
        {
          "bg-slate-200": isSelected,
        }
      )}
    >
      <div className="truncate flex-1 min-w-0" ref={titleRef}>
        {title}
      </div>
      <div className="shrink-0 hidden group-hover:block">
        <DeleteAction
          iconOnly
          data={{ ID: thread.id, name: title }}
          entityName={t("thread")}
          deleteCallback={(args) => {
            const response = deleteThread(args);
            onDelete(thread.id);

            return response;
          }}
        />
      </div>
    </div>
  );

  return (
    <button
      disabled={isDisabled}
      onClick={onClick}
      aria-label={t("thread_label", { title })}
      className="box-border !mx-2"
    >
      {isOverflowing ? (
        <Tooltip title={title} placement="bottom-start" enterDelay={500}>
          <div className="w-full min-w-0">{innerItem}</div>
        </Tooltip>
      ) : (
        innerItem
      )}
    </button>
  );
};

export default ThreadListItem;
